/**
 * HR Documents module theme — aliases app brand (Patient indigo SoT).
 *
 * Prefer importing from here in Documents UI so the module stays swappable
 * without reaching into Patients/. Color changes still flow from patientTheme.
 */
import { BRAND_THEME, BRAND_THEME_HEX } from "../../ui/brandTheme";

export const DOCUMENT_THEME = BRAND_THEME;
export const DOCUMENT_THEME_HEX = BRAND_THEME_HEX;

/** Shared role chip — one brand tone (not per-role rainbow). */
export const DOCUMENT_ROLE_CHIP =
  "bg-indigo-50 text-indigo-700 border border-indigo-100";

/**
 * Compact row actions — same density as Patient list "View" (h-6 / 11px).
 */
export const DOCUMENT_BTN = {
  soft: `inline-flex items-center justify-center gap-1 h-6 px-2 text-[11px] font-medium rounded border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${BRAND_THEME.button.soft}`,
  outline: `inline-flex items-center justify-center gap-1 h-6 px-2 text-[11px] font-medium rounded border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${BRAND_THEME.button.outline}`,
  primary: `inline-flex items-center justify-center gap-1 h-6 px-2 text-[11px] font-semibold rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${BRAND_THEME.button.primary}`,
  danger:
    "inline-flex items-center justify-center gap-1 h-6 px-2 text-[11px] font-medium rounded border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
  link: `inline-flex items-center gap-1 text-[11px] font-medium ${BRAND_THEME.button.link}`,
  modalPrimary: `inline-flex items-center justify-center gap-1.5 h-9 px-3 text-sm font-semibold rounded-md transition-colors disabled:opacity-50 ${BRAND_THEME.button.primary}`,
  modalCancel:
    "inline-flex items-center justify-center gap-1.5 h-9 px-3 text-sm font-medium rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50",
};

/** Form field chrome for document modals (Patient density). */
export const DOCUMENT_FIELD = {
  label: "block text-[11px] font-medium text-slate-700 mb-1",
  input: `w-full h-9 px-3 text-sm rounded-md border border-indigo-200 bg-white text-slate-800 outline-none transition-colors ${BRAND_THEME.fieldFocus}`,
  inputDisabled:
    "w-full h-9 px-3 text-sm rounded-md border border-indigo-100 bg-indigo-50/40 text-slate-500 cursor-not-allowed outline-none",
  textarea: `w-full px-3 py-2 text-sm rounded-md border border-indigo-200 bg-white text-slate-800 outline-none transition-colors resize-y min-h-[4.5rem] ${BRAND_THEME.fieldFocus}`,
  error: "mt-1 text-[11px] text-red-600",
};

/** Modal shell tokens (Patient AddPhysician pattern). */
export const DOCUMENT_MODAL = {
  backdrop:
    "fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40",
  panel:
    "w-full max-w-lg rounded-xl border border-indigo-200 bg-white shadow-xl overflow-hidden max-h-[90vh] flex flex-col",
  head: `flex items-start justify-between gap-3 px-4 py-3 border-b shrink-0 ${BRAND_THEME.modalHead}`,
  title: `text-sm font-semibold uppercase tracking-wide ${BRAND_THEME.modalTitle}`,
  subtitle: "mt-0.5 text-[11px] text-slate-600",
  body: "p-4 space-y-3 overflow-y-auto flex-1",
  footer:
    "flex items-center justify-end gap-2 px-4 py-3 border-t border-indigo-100 bg-indigo-50/40 shrink-0",
  close:
    "inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-indigo-50 hover:text-indigo-700 transition-colors",
};

/**
 * Column widths for `table-fixed` layouts.
 *
 * Leading controls (checkbox / Sr. No.):
 * - Use {@link DOCUMENT_COL.lead} with Table `selection` + `showSrNo`
 *   (Table merges them into one column by default).
 * - Use {@link DOCUMENT_COL.srNo} only when there is no selection column.
 * Percentages below target ~100% excluding the fixed rem lead column.
 */
export const DOCUMENT_COL = {
  /**
   * Combined checkbox + Sr. — pass as Table `selectionColClassName`.
   * Fixed rem width so `table-fixed` never stretches them apart.
   */
  lead: "w-[4.75rem] min-w-[4.75rem] max-w-[5rem] pl-2.5 pr-1.5 text-left align-middle",
  /** Checkbox-only (selection without Sr. No.). */
  select: "w-9 min-w-[2.25rem] px-2 text-center align-middle",
  /** Sr. No. only (no selection) — tight leading column. */
  srNo: "w-9 min-w-[2.25rem] max-w-[2.5rem] px-2 text-center align-middle",
  name: "w-[40%] min-w-0 pl-3 pr-3 align-middle",
  nameDense: "w-[22%] min-w-0 pl-3 pr-3 align-middle",
  status: "w-[12%] whitespace-nowrap px-3 align-middle",
  date: "w-[14%] whitespace-nowrap px-3 align-middle tabular-nums",
  note: "w-[18%] min-w-0 px-3 align-middle",
  upload: "w-[18%] whitespace-nowrap px-3 align-middle",
  /** View + Delete / Upload New need room inside fixed tables. */
  actions: "w-[22%] whitespace-nowrap pl-2 pr-4 text-right align-middle",
  /** HR Documents — employee picker list (balanced with Sr. column). */
  empName: "w-[26%] min-w-0 pl-3 pr-2 align-middle",
  empEmail: "w-[32%] min-w-0 px-2 align-middle",
  empRole: "w-[10%] whitespace-nowrap px-2 align-middle",
  empStatus: "w-[10%] whitespace-nowrap px-2 align-middle",
  empActions: "w-[14%] min-w-[5.5rem] whitespace-nowrap pl-2 pr-4 text-right align-middle",
  /** History modal (tax / credentials) — Actions never clipped. */
  histForm: "w-[18%] min-w-0 pl-3 pr-3 align-middle",
  histName: "w-[28%] min-w-0 pl-3 pr-3 align-middle",
  histNote: "w-[28%] min-w-0 px-3 align-middle",
  histDate: "w-[14%] whitespace-nowrap px-3 align-middle tabular-nums",
  histActions: "w-[28%] whitespace-nowrap pl-2 pr-3 text-right align-middle",
  /** Service Log — same combined lead as Table. */
  logLead:
    "w-[4.75rem] min-w-[4.75rem] max-w-[5rem] pl-2.5 pr-1.5 text-left align-middle",
  logSrNo: "w-9 min-w-[2.25rem] max-w-[2.5rem] px-2 text-center align-middle",
  logDate: "w-[12%] whitespace-nowrap px-3 align-middle tabular-nums",
  logType: "w-[28%] min-w-0 px-3 align-middle",
  logNote: "w-[22%] min-w-0 px-3 align-middle",
  logSign: "w-[20%] min-w-0 px-3 align-middle",
  logActions: "w-[12%] whitespace-nowrap pl-2 pr-3 text-right align-middle",
};

/** Service Log add-row / inline edit field chrome. */
export const SERVICE_LOG_FIELD = {
  select: `h-9 min-w-[11rem] px-3 text-sm rounded-md border border-indigo-200 bg-white text-slate-800 outline-none cursor-pointer transition-colors ${BRAND_THEME.fieldFocus}`,
  note: `flex-1 min-w-[10rem] h-9 px-3 text-sm rounded-md border border-indigo-200 bg-white text-slate-800 placeholder:text-slate-400 outline-none transition-colors ${BRAND_THEME.fieldFocus}`,
  date: `h-9 px-3 pr-10 text-sm rounded-md border border-indigo-200 bg-white text-slate-800 outline-none transition-colors ${BRAND_THEME.fieldFocus}`,
  rowSelect: `w-full h-8 px-2 text-sm rounded-md border border-indigo-200 bg-white text-slate-800 outline-none cursor-pointer ${BRAND_THEME.fieldFocus}`,
  rowInput: `w-full h-8 px-2 text-sm rounded-md border border-indigo-200 bg-white text-slate-800 outline-none ${BRAND_THEME.fieldFocus}`,
};

export function documentStatusClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "completed" || s === "active") {
    return "bg-emerald-50 text-emerald-700 border-emerald-200";
  }
  if (s === "pending" || s === "draft" || !s) {
    return "bg-amber-50 text-amber-700 border-amber-200";
  }
  if (s === "to_be_corrected") {
    return "bg-orange-50 text-orange-700 border-orange-200";
  }
  if (s === "send_to_office" || s === "filled") {
    return "bg-indigo-50 text-indigo-700 border-indigo-200";
  }
  if (s === "inactive") {
    return "bg-slate-50 text-slate-600 border-slate-200";
  }
  return "bg-slate-50 text-slate-600 border-slate-200";
}

export function formatDocumentStatus(status) {
  const s = String(status || "");
  const map = {
    completed: "Completed",
    pending: "Pending",
    to_be_corrected: "To Be Corrected",
    send_to_office: "Sent to Office",
    filled: "Sent to Office",
    active: "Active",
    inactive: "Inactive",
    draft: "Draft",
  };
  if (!s) return "Pending";
  return (
    map[s.toLowerCase()] ||
    s
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ")
  );
}
